<template>
  <div class="app-header flex-row">
    <div class="icon-box h-full flex-row items-center">
      <SvgIcon class="logo-icon" localIcon="logo" @click="handleLogoClick" />
      <div class="flex-col ml-2 logo-text-container">
        <span class="logo-text">American </span>
        <span class="logo-text">Restaurant Alliance</span>
      </div>
    </div>

    <!-- 桌面端导航菜单 -->
    <nav class="nav-menu desktop-menu">
      <el-menu 
        ref="desktopMenuRef"
        mode="horizontal" 
        :ellipsis="false" 
        :default-active="activeMenuIndex"
        class="nav-menu-list"
      >
        <template v-for="(item, index) in navItems" :key="index">
          <el-sub-menu v-if="item.hasDropdown && item.children?.length" :index="String(index)">
            <template #title>
              <div class="nav-item flex-row items-center" :class="{ active: isActive(item) }">
                <span class="nav-text">{{ $t(item.textKey) }}</span>
                <div class="nav-indicator"></div>
              </div>
            </template>
            <el-menu-item v-for="(child, childIndex) in item.children" :key="childIndex" :index="child.route"
              @click="handleNavClick(child)">
              <div class="z-10 w-full pl-2 sub-nav-text" :class="{ active: isActive(child) }">{{ $t(child.textKey) }}</div>
            </el-menu-item>
          </el-sub-menu>

          <el-menu-item v-else :index="String(index)">
            <div class="nav-item flex-row items-center" :class="{ active: isActive(item) }"
              @click="handleNavClick(item)">
              <span class="nav-text">{{ $t(item.textKey) }}</span>
              <div class="nav-indicator"></div>
            </div>
          </el-menu-item>
        </template>
      </el-menu>

      <div class="lang-switch-container">
        <LangSwitch />
      </div>
    </nav>

    <!-- 移动端汉堡菜单按钮 -->
    <div class="mobile-menu-button" @click="drawerVisible = true">
      <SvgIcon icon="heroicons:bars-3" class="menu-icon" />
    </div>
  </div>

  <!-- 移动端侧边菜单抽屉 - 移到组件外部以避免高度限制 -->
  <Teleport to="body">
    <div class="drawer-wrapper" v-if="drawerVisible">
      <div class="drawer-overlay" @click="drawerVisible = false"></div>
      <div class="custom-drawer">
        <div class="drawer-header flex-row justify-between items-center">
          <div class="logo-container flex-row items-center" @click="handleLogoClickInDrawer">
            <SvgIcon localIcon="logo" class="drawer-logo" />
            <div class="flex-col ml-2">
              <span class="logo-text">American </span>
              <span class="logo-text">Restaurant Alliance</span>
            </div>
          </div>
          <div class="close-button" @click="drawerVisible = false">
            <SvgIcon icon="heroicons:x-mark" class="close-icon" />
          </div>
        </div>

        <div class="drawer-content">
          <el-menu 
            ref="mobileMenuRef"
            mode="vertical" 
            :default-active="activeMenuIndex"
            class="drawer-menu"
          >
            <template v-for="(item, index) in navItems" :key="index">
              <el-sub-menu v-if="item.hasDropdown && item.children?.length" :index="String(index)">
                <template #title>
                  <div class="drawer-nav-item" :class="{ active: isActive(item) }">
                    {{ $t(item.textKey) }}
                  </div>
                </template>
                <el-menu-item v-for="(child, childIndex) in item.children" :key="childIndex"
                  @click="handleNavClickInDrawer(child)">
                  <div class="drawer-nav-item" :class="{ active: isActive(child) }">
                    {{ $t(child.textKey) }}
                  </div>
                </el-menu-item>
              </el-sub-menu>

              <el-menu-item v-else :index="String(index)" @click="handleNavClickInDrawer(item)">
                <div class="drawer-nav-item" :class="{ active: isActive(item) }">
                  {{ $t(item.textKey) }}
                </div>
              </el-menu-item>
            </template>
          </el-menu>

          <!-- 抽屉中的语言切换 -->
          <div class="drawer-lang-switch flex-row items-center">
            <span class="lang-label">{{ $t('settings.language.title') }}:</span>
            <LangSwitch />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import LangSwitch from '@/components/common/lang-switch.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { useRouteStore } from '@/store/modules/route';

const router = useRouter();
const route = useRoute();
const routeStore = useRouteStore();
const { navItems } = storeToRefs(routeStore);

// 侧边栏抽屉可见性
const drawerVisible = ref(false);

// 菜单组件引用
const desktopMenuRef = ref();
const mobileMenuRef = ref();

// 获取当前路径
const currentPath = computed(() => route.path);

/** 根据当前路由获取活动菜单索引 */
function getActiveMenuIndex(): string {
  const current = currentPath.value;
  
  // 遍历导航项查找匹配的菜单
  for (let i = 0; i < navItems.value.length; i++) {
    const item = navItems.value[i];
    
    // 检查主菜单项是否激活
    if (isActive(item)) {
      // 如果有子菜单且子菜单激活，返回子菜单的route
      if (item.children?.length) {
        for (const child of item.children) {
          if (isActive(child)) {
            return child.route || String(i);
          }
        }
      }
      // 返回主菜单项索引
      return String(i);
    }
  }
  
  return '';
}

// 计算当前活动的菜单索引
const activeMenuIndex = computed(() => getActiveMenuIndex());

/** 判断导航项是否被激活 */
function isActive(item: any): boolean {
  const itemRoute = item.route || '';
  const current = currentPath.value;

  // 1. 精确匹配当前路径
  const exactMatch = itemRoute && current === itemRoute;

  // 2. 处理首页路径特殊匹配
  const homeMatch = itemRoute === '/' && current === '/home';

  // 3. 重定向路径匹配
  const redirectMatch = item.redirect && current === item.redirect;

  // 4. 智能前缀匹配（处理子路径）
  const pathPrefixMatch = itemRoute && itemRoute !== '/' &&
    (current.startsWith(`${itemRoute}/`) || current === itemRoute);

  // 5. 子项激活检查（递归检查所有后代）
  const childActive = (item.children || []).some((child: any) => {
    const childRoute = child.route || '';
    return current === childRoute ||
      current.startsWith(`${childRoute}/`) ||
      isActive(child); // 递归检查子项
  });

  // 6. 特殊路径处理（如/contact需要单独处理）
  const specialPathMatch = current.startsWith('/contact') && itemRoute === '/contact';

  return Boolean(
    exactMatch ||
    homeMatch ||
    redirectMatch ||
    pathPrefixMatch ||
    childActive ||
    specialPathMatch
  );
}

/** 处理Logo点击 */
function handleLogoClick() {
  router.push('/');
}

/** 处理抽屉中Logo点击 */
function handleLogoClickInDrawer() {
  router.push('/');
  drawerVisible.value = false;
}

/** 处理导航项点击 */
function handleNavClick(item: any) {
  const toRoute = item.redirect || item.route;
  // 如果item.route为空，则不进行跳转
  if (!toRoute) {
    return;
  }
  // 跳转
  routeStore.navigateTo(toRoute, item.onClick);
}

/** 处理抽屉中导航项点击 */
function handleNavClickInDrawer(item: any) {
  const toRoute = item.redirect || item.route;
  // 如果item.route为空，则不进行跳转
  if (!toRoute) {
    return;
  }
  // 跳转并关闭抽屉
  routeStore.navigateTo(toRoute, item.onClick);
  drawerVisible.value = false;
}

/** 更新菜单活动索引 */
function updateMenuActiveIndex() {
  const activeIndex = activeMenuIndex.value;
  
  nextTick(() => {
    // 更新桌面端菜单
    if (desktopMenuRef.value && typeof desktopMenuRef.value.updateActiveIndex === 'function') {
      desktopMenuRef.value.updateActiveIndex(activeIndex);
    }
    
    // 更新移动端菜单
    if (mobileMenuRef.value && typeof mobileMenuRef.value.updateActiveIndex === 'function') {
      mobileMenuRef.value.updateActiveIndex(activeIndex);
    }
  });
}

// 监听路由变化，更新菜单活动状态
watch(
  () => route.path,
  () => {
    updateMenuActiveIndex();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
@use '@/styles/scss/typography' as *;
@use '@/styles/scss/layout' as *;

.app-header {
  @extend %gradient-header;
  position: relative;
  border-bottom: 1px solid var(--border-color);
  height: var(--header-height, 60px);
  padding: 0 24px;
  background-color: #FFFFFF75;
  background-image: radial-gradient(transparent 3px, #fff 1px);
  background-size: 4px 4px;
  backdrop-filter: saturate(50%) blur(4px);
  -webkit-backdrop-filter: saturate(50%) blur(4px);
  top: 0;
  z-index: 1000;
  justify-content: space-between;

  .logo-icon {
    font-size: 50px;
    cursor: pointer;
  }

  .logo-text {
    font-size: 16px;
    color: #333;
    font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  .nav-menu {
    display: flex;
    margin-left: auto;
    align-items: center;

    .nav-menu-list {
      border: none;
      background: transparent;

      :deep(.el-menu-item),
      :deep(.el-sub-menu__title) {
        padding: 0 1.5vw;
        border: none !important;
        background: transparent;
        height: var(--header-height, 50px);
        line-height: var(--header-height, 50px);

        &::before {
          display: none !important;
        }

        &:hover,
        &:focus {
          background-color: #FFFFFFE0;
        }

        .nav-item {
          height: 22px;
          position: relative;
          cursor: pointer;
          gap: 4px;

          &.active {
            .nav-text {
              @extend %nav-text-active;
            }

            .nav-indicator {
              @extend %gradient-primary;
            }
          }

          &:not(.active) {
            .nav-text {
              @extend %nav-text;
            }

            .nav-indicator {
              opacity: 0;
            }

            &:hover {
              .nav-indicator {
                opacity: 0.5;
                @extend %gradient-primary;
              }
            }
          }

          .nav-text {
            height: 20px;
            text-align: center;
            white-space: nowrap;
          }

          .nav-indicator {
            position: absolute;
            bottom: -5px;
            left: 0;
            right: 0;
            height: 2px;
            border-radius: $border-radius-sm;
            transition: opacity 0.3s ease;
          }
        }
      }

      :deep(.el-sub-menu__icon-arrow) {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 5px;
      }
    }
  }

  // 移动端汉堡菜单按钮
  .mobile-menu-button {
    display: none;
    width: 48px;
    height: 48px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 50%;
    background-color: rgba(var(--primary-color), 0.1);
    transition: background-color 0.3s ease;

    &:hover {
      background-color: rgba(var(--primary-color), 0.2);
    }

    .menu-icon {
      font-size: 28px;
      color: rgb(var(--primary-color));
    }
  }
}

.lang-switch-container {
  position: relative;
  display: flex;
  align-items: center;
  height: 22px;
  margin: 0 0 0 1.5vw;

  :deep() {
    .lang-switch-container {
      height: 100%;
      display: flex;
      align-items: center;
      gap: 4px;

      .current-lang {
        @extend %nav-text;
        line-height: 1;
      }

      .icon-arrow {
        font-size: 6px;
        margin-top: 1px;
        transform: rotate(90deg);
      }
    }

    .lang-options {
      top: calc(100% + 8px);
      right: 0;
      min-width: 80px;
    }
  }

  &:hover {
    .nav-indicator {
      opacity: 0.5;
      @extend %gradient-primary;
    }
  }
}

.sub-nav-text {
  &.active {
    @extend %nav-text-active;
  }
}

// 自定义抽屉样式
.drawer-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;

  .drawer-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }

  .custom-drawer {
    position: absolute;
    top: 0;
    right: 0;
    width: 80%;
    height: 100%;
    background-color: white;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    animation: slideIn 0.3s ease;

    .drawer-header {
      padding: 16px;
      border-bottom: 1px solid var(--border-color);

      .drawer-logo {
        font-size: 40px;
        cursor: pointer;
      }

      .close-button {
        width: 40px;
        height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        border-radius: 50%;
        background-color: rgba(var(--primary-color), 0.1);
        transition: background-color 0.3s ease;

        &:hover {
          background-color: rgba(var(--primary-color), 0.2);
        }

        .close-icon {
          font-size: 24px;
          color: rgb(var(--primary-color));
        }
      }
    }

    .drawer-content {
      flex: 1;
      padding: 16px 0;
      overflow-y: auto;

      .drawer-menu {
        border-right: none;
        background-color: transparent;

        .drawer-nav-item {
          padding: 12px 16px;
          font-size: 16px;
          color: #333;
          cursor: pointer;
          position: relative;

          &.active {
            color: rgb(var(--primary-color));
            font-weight: 500;
          }
        }
      }

      .drawer-lang-switch {
        padding: 16px;
        border-top: 1px solid var(--border-color);
        margin-top: 16px;

        .lang-label {
          margin-right: 16px;
          font-size: 16px;
          color: #333;
        }
      }
    }
  }
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}

// 响应式布局
@media (max-width: 768px) {
  .app-header {
    padding: 0 16px;

    .logo-text-container {
      display: none;
    }

    .desktop-menu {
      display: none;
    }

    .mobile-menu-button {
      display: flex;
    }
  }
}
</style>